import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButtonProps, Tooltip } from '@mui/material'
import { Edit } from '@mui/icons-material'
import { GridActionsCellItem } from '@mui/x-data-grid'
import { Device, Project, RuuviTag } from '../types'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import { usePolicy } from '../hooks'
import useDialog from '../hooks/useDialog'
import AlertRuleEditDialog from './AlertRuleEditDialog'
import { AlertRuleDataGridRow } from '../types/AlertRuleDataGridRow'

export interface AlertRuleEditButtonProps extends Omit<IconButtonProps, 'children' | 'onClick'> {
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  alertRules: AlertGroup[]
  alertTemplates: AlertTemplate[]
  rows: AlertRuleDataGridRow[]
  row: AlertRuleDataGridRow
  useRedux?: boolean
  beforeUpdate?: (alertRules: AlertGroup[]) => void
  afterUpdate?: (alertRules: AlertGroup[]) => void
}

export default function AlertRuleEditButton({
  project,
  device,
  ruuviTag,
  alertRules,
  alertTemplates,
  rows,
  row,
  size = 'small',
  useRedux = true,
  beforeUpdate,
  afterUpdate,
  ...props
}: AlertRuleEditButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The policy state.
   */
  const { canEditProjectAlertRules, canEditDeviceAlertRules, canEditRuuviTagAlertRules } =
    usePolicy()

  /**
   * Indicates if the current user can edit alert rules.
   */
  const canEditAlertRules =
    (project && canEditProjectAlertRules(project)) ||
    (device && canEditDeviceAlertRules(device)) ||
    (ruuviTag && canEditRuuviTagAlertRules(ruuviTag))

  /**
   * Indicates if the alert rule is being saved.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * Handle before submit.
   */
  function handleBeforeSubmit(alertRules: AlertGroup[]) {
    beforeUpdate?.(alertRules)
    setSubmitting(true)
  }

  /**
   * Handle after submit.
   */
  function handleAfterSubmit(alertRules: AlertGroup[]) {
    afterUpdate?.(alertRules)
    setSubmitting(false)
    closeDialog()
  }

  return (
    <>
      {canEditAlertRules && (
        <Tooltip title={t('edit')}>
          <GridActionsCellItem
            label={t('edit')}
            icon={<Edit fontSize={size} />}
            disabled={isSubmitting}
            {...props}
            onClick={openDialog}
          />
        </Tooltip>
      )}

      <AlertRuleEditDialog
        alertTemplates={alertTemplates}
        alertRules={alertRules}
        rows={rows}
        row={row}
        project={project}
        device={device}
        ruuviTag={ruuviTag}
        open={open}
        useRedux={useRedux}
        beforeSubmit={handleBeforeSubmit}
        afterSubmit={handleAfterSubmit}
        onClose={closeDialog}
      />
    </>
  )
}
